import type { Permission } from '@playplate/shared';
import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Shell } from './components/Shell';
import { Loading } from './components/ui';
import { useAuth } from './hooks/useAuth';
import { AlertDetailPage, AlertsPage } from './pages/Alerts';
import { CamerasPage } from './pages/Cameras';
import { DashboardPage } from './pages/Dashboard';
import { EventsPage } from './pages/Events';
import { LiveViewPage } from './pages/LiveView';
import { LoginPage } from './pages/Login';
import { SettingsPage } from './pages/Settings';
import { SystemPage } from './pages/System';
import { UsersPage } from './pages/Users';
import { ZonesPage } from './pages/Zones';

// Charting is ~380 kB of the bundle and only this screen needs it. Loading it
// lazily keeps the Command Center's first paint fast on a wall tablet.
const AnalyticsPage = lazy(() =>
  import('./pages/Analytics').then((module) => ({ default: module.AnalyticsPage })),
);

/**
 * Route guard.
 *
 * This decides what to *render*, never what is allowed. Every route behind it
 * calls an API that re-checks the same permission server-side, so a user who
 * edits their way past this sees an empty screen and a 403, not data.
 */
function RequireAuth({ permission, children }: { permission?: Permission; children: JSX.Element }) {
  const { user, loading, can } = useAuth();
  const location = useLocation();

  if (loading) return <Loading label="Checking your session" />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  if (permission && !can(permission)) {
    return (
      <div className="panel px-6 py-12 text-center">
        <h1 className="font-display text-lg">Not available for your role</h1>
        <p className="mt-2 text-sm text-muted">
          Your account does not have the <code className="font-mono">{permission}</code> permission.
        </p>
      </div>
    );
  }

  return children;
}

export function App() {
  const { user, loading } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={loading ? <Loading /> : user ? <Navigate to="/" replace /> : <LoginPage />}
      />

      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route
          path="/cameras"
          element={
            <RequireAuth permission="camera:read">
              <CamerasPage />
            </RequireAuth>
          }
        />
        <Route
          path="/live"
          element={
            <RequireAuth permission="camera:stream">
              <LiveViewPage />
            </RequireAuth>
          }
        />
        <Route
          path="/events"
          element={
            <RequireAuth permission="event:read">
              <EventsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/alerts"
          element={
            <RequireAuth permission="alert:read">
              <AlertsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/alerts/:id"
          element={
            <RequireAuth permission="alert:read">
              <AlertDetailPage />
            </RequireAuth>
          }
        />
        <Route
          path="/analytics"
          element={
            <RequireAuth permission="analytics:read">
              <Suspense fallback={<Loading label="Loading analytics" />}>
                <AnalyticsPage />
              </Suspense>
            </RequireAuth>
          }
        />
        <Route
          path="/zones"
          element={
            <RequireAuth permission="zone:read">
              <ZonesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/users"
          element={
            <RequireAuth permission="user:read">
              <UsersPage />
            </RequireAuth>
          }
        />
        <Route
          path="/system"
          element={
            <RequireAuth permission="system:read">
              <SystemPage />
            </RequireAuth>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireAuth permission="settings:read">
              <SettingsPage />
            </RequireAuth>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
